import useSWR from "swr";
import { Problem, Summary } from "@/models/summary";
import ApiService, { defaultSWROption } from "@/services/api/api.service";
import { SummarySchema } from "./summary.api.types";
import SummaryEndpoints from "./summary.endpoint";

const urlSafeBase64Encode = (str: string): string => {
  // UTF-8エンコード
  const utf8Bytes = new TextEncoder().encode(str);
  // Uint8Arrayを通常の配列に変換
  const byteArray = Array.from(utf8Bytes);
  // 各バイトを文字に変換し、結合
  const utf8String = String.fromCharCode.apply(null, byteArray);
  // Base64エンコード
  const b64String = btoa(utf8String);
  return b64String.replace(/\+/g, "-").replace(/\//g, "_");
};

export class SummaryService {
  static getSummary(
    analysisId: string | undefined,
    categoryPath: string | undefined,
    isCategoryValid: boolean = true,
  ): {
    data: Summary | undefined;
    error: unknown;
    isLoading: boolean;
  } {
    const encodedCategoryPath = categoryPath
      ? urlSafeBase64Encode(categoryPath)
      : undefined;

    const fetcher = () =>
      ApiService.fetchData<undefined, SummarySchema>({
        url: analysisId && encodedCategoryPath
          ? SummaryEndpoints.GET_SUMMARY(analysisId, encodedCategoryPath)
          : "",
        method: "GET",
      });

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { data, error, isLoading } = useSWR(
      analysisId && encodedCategoryPath && isCategoryValid
        ? SummaryEndpoints.GET_SUMMARY(analysisId, encodedCategoryPath)
        : null,
      fetcher,
      {
        ...defaultSWROption,
        onError: (error) => {
          // 404はエラーではない（該当する結果が存在しないだけの正常系と捉える）なのでエラーポップアップを表示しない
          if (error?.response?.status === 404) {
            return;
          }
          // 404以外のエラーは通常のエラーハンドリング
          defaultSWROption.onError?.(error);
        },
      },
    );

    return {
      data: data ? buildSummary(data.data) : undefined,
      error,
      isLoading,
    };
  }
}

const buildSummary = (summary: SummarySchema) => {
  return new Summary(
    summary.summary,
    summary.num_voc,
    summary.problems.map(
      (problem, index) =>
        new Problem(
          problem,
          summary.problem_details[index],
          summary.counts[index],
          summary.suggestions[index],
        ),
    ),
  );
};
